import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Box,
  IconButton,
  Tooltip,
  InputAdornment,
  Typography,
  Paper,
  Divider,
  Button,
} from '@mui/material';
import { Close, Search } from '@mui/icons-material';
import { muiIconsList, MuiIconDefinition } from './muiIcons';
import { useDebounce } from '@uidotdev/usehooks';
import intl from 'components/i18n/ReactIntlWrapper';

interface MuiIconPickerProps {
  open: boolean;
  onClose: () => void;
  onSelect: (iconName: string) => void;
  currentIcon?: string;
  color?: string;
}

const MuiIconPicker: React.FC<MuiIconPickerProps> = ({
  open,
  onClose,
  onSelect,
  currentIcon,
  color = 'inherit',
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 300);
  const [displayLimit, setDisplayLimit] = useState(200); // Show 200 icons initially

  // Filter icons based on search query
  const filteredIcons = useMemo(() => {
    const trimmedQuery = (debouncedSearch || '').trim();
    
    if (!trimmedQuery) {
      return muiIconsList.slice(0, displayLimit); // Limit initial display
    }

    const lowerQuery = trimmedQuery.toLowerCase();
    const filtered = muiIconsList.filter((icon) => {
      // Search in icon name and keywords
      return (
        icon.name.toLowerCase().includes(lowerQuery) ||
        icon.keywords.some((keyword) => keyword.includes(lowerQuery))
      );
    });
    
    // Show all results when searching, but limit to 500 max for performance
    return filtered.slice(0, 500);
  }, [debouncedSearch, displayLimit]);

  // Reset display limit when search changes
  React.useEffect(() => {
    if (debouncedSearch) {
      setDisplayLimit(500);
    } else {
      setDisplayLimit(200);
    }
  }, [debouncedSearch]);

  const handleSelect = (iconName: string) => {
    onSelect(iconName);
    onClose();
    setSearchQuery(''); // Reset search on close
  };

  const handleClose = () => {
    onClose();
    setSearchQuery(''); // Reset search on close
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          height: '80vh',
          maxHeight: '800px',
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">{intl.get('muiIconPicker.title')}</Typography>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent>
        <Box mb={2}>
          <TextField
            fullWidth
            placeholder={intl.get('muiIconPicker.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            variant="outlined"
            size="small"
            autoFocus
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }
            }}
          />
          <Box mt={1} display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="caption" color="text.secondary">
              {intl.get('muiIconPicker.showing', { 
                count: filteredIcons.length, 
                total: debouncedSearch ? muiIconsList.filter((icon) => {
                  const lowerQuery = debouncedSearch.toLowerCase();
                  return icon.name.toLowerCase().includes(lowerQuery) || 
                         icon.keywords.some((keyword) => keyword.includes(lowerQuery));
                }).length : muiIconsList.length 
              })}
            </Typography>
            {currentIcon && (
              <Typography variant="caption" color="primary">
                {intl.get('muiIconPicker.current', { icon: currentIcon })}
              </Typography>
            )}
          </Box>
        </Box>

        <Paper 
          variant="outlined" 
          sx={{ 
            height: 'calc(100% - 100px)',
            overflow: 'auto',
            p: 1,
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(60px, 1fr))',
              gap: 1,
            }}
          >
            {filteredIcons.map((icon: MuiIconDefinition) => {
              const IconComponent = icon.component;
              const isSelected = currentIcon === icon.name;

              return (
                <Tooltip title={icon.name} arrow placement="top" key={icon.name}>
                  <Paper
                    elevation={isSelected ? 4 : 0}
                    sx={{
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      border: isSelected ? '2px solid' : '1px solid',
                      borderColor: isSelected ? 'primary.main' : 'divider',
                      bgcolor: isSelected ? 'action.selected' : 'background.paper',
                      '&:hover': {
                        bgcolor: 'action.hover',
                          borderColor: 'primary.main',
                          transform: 'scale(1.1)',
                        },
                      }}
                      onClick={() => handleSelect(icon.name)}
                    >
                      <IconComponent 
                        sx={{ 
                          fontSize: 24,
                          color: isSelected ? 'primary.main' : color,
                        }} 
                      />
                    </Paper>
                  </Tooltip>
              );
            })}
          </Box>

          {/* Load More Button */}
          {!debouncedSearch && displayLimit < muiIconsList.length && (
            <Box mt={2} display="flex" justifyContent="center">
              <Button 
                variant="outlined" 
                onClick={() => setDisplayLimit(prev => Math.min(prev + 200, muiIconsList.length))}
              >
                {intl.get('muiIconPicker.loadMore', { remaining: muiIconsList.length - displayLimit })}
              </Button>
            </Box>
          )}

          {filteredIcons.length === 0 && (
            <Box 
              display="flex" 
              alignItems="center" 
              justifyContent="center" 
              height="100%"
            >
              <Typography variant="body2" color="text.secondary">
                {intl.get('muiIconPicker.noResults', { query: searchQuery })}
              </Typography>
            </Box>
          )}
        </Paper>
      </DialogContent>
    </Dialog>
  );
};

export default MuiIconPicker;
